// ItemPage — /items/:itemId. §6.5, and the answer to the second half of the
// complaint:
//
//   Miles: "It should have a 'Full Details' button to display absolutely
//   everything about that item that we could possibly want to see in a new
//   page."
//
// "Absolutely everything" is two things joined. The DOCUMENT side comes from
// the WEL-144 viewer — the bespoke panel, the document itself inline in
// whatever state it is in (partial PDF while in flight, WEL-29), every version,
// the form replica for form kinds, fill-on-behalf. The SIGNING side is
// everything the thread page carried and nothing else does: the native rail,
// the submissions spine with per-signer status and email swap, Remind / Recall
// / Revise / Void / Cancel, the quiet swap, the signed PDF and audit
// certificate, the occupancy-sync chip.
//
// This page is where those two halves meet, and after W7 it is the ONLY place
// the signing half exists. Everything below that came from ThreadDetailPage was
// ported with its client calls unchanged — same endpoints, same bodies, same
// consequence copy — because the payloads are the contract (§11 item 13) and a
// rewritten one is a silent behaviour change.
//
// The block ORDER is the panel's, not this page's: §6.4's state-dependent order
// with the whole document standing where the document block would be. That is
// why `SentItemPanel` is mounted in two segments — the blocks before the
// document, the viewer, then the blocks after it. In motion nothing comes
// after; finished, recipients and activity do.
//
// One rule this page does NOT relax: it never calls /api/cc/thread. Everything
// it shows comes from GET /api/sent-items/:itemId (§7.2), which is the merge of
// the two loaders server-side. A second fetch here would be a second source of
// truth for the same row.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ApiError } from '@/lib/api';
import { toast } from '@/components/Toast';
import { AppfolioSyncChip } from '@/components/AppfolioSyncChip';
import { SignerEmailSwap } from '@/components/SignerEmailSwap';
import { ConsequenceConfirm } from '@/components/oc/ConsequenceConfirm';
import { ChoiceDialog } from '@/components/oc/ChoiceDialog';
import {
  isSubmissionOpen,
  LifecycleRailView,
  OcEmpty,
  OcError,
  OcLoading,
  SignalRowView,
  signerTone,
  StatusChip,
  submissionTone,
} from '@/components/oc/bits';
import { ViewDocViewer } from '@/components/viewDocs/ViewDocViewer';
import { leaseViewPath } from '@/lib/leaseViewPath';
import { ViewDocVersionStrip } from '@/components/viewDocs/ViewDocVersionStrip';
import { SentItemIcon } from '@/components/sentItems/SentItemIcon';
import { ActionButtons, SentItemPanel, stripVersions } from '@/components/sentItems/SentItemPanel';
import {
  glyphForKind,
  panelBlockOrder,
  relativeTime,
  restartActions,
  rowInMotion,
  rowStateTone,
  whereLabel,
  type PanelBlock,
} from '@/components/sentItems/sentItemsView';
import { describeWhere, RESTRICTED_ROW_NOTE } from '@/lib/viewDocs';
import {
  formatAge,
  formatWhen,
  quietSwapThread,
  requestConditionReportNewSignatures,
  resolveConditionReportWithdrawal,
  withdrawConditionReportCorrection,
  type SubmissionRow,
  type SubmissionSigner,
} from '@/lib/commandCenterClient';
import {
  CORRECTION_COPY,
  WHAT_CHANGED_MAX,
  correctionRequestedLine,
  correctionRequestedToast,
  correctionTookEffectLine,
  correctionWhatChangedLine,
} from '@/lib/conditionReportCorrection';
import {
  cancelOwnerIntake,
  resendOwnerIntake,
  swapOwnerIntakeOwnerEmail,
  OwnerIntakeNotEnabledError,
} from '@/lib/ownerIntakeClient';
import {
  getAuditCertUrl,
  getSignedPdfUrl,
  reviseLease,
  updateSignerEmail,
  voidLeaseVersion,
  type CurrentDocumentType,
} from '@/lib/leaseDraftsClient';
import { cancelRequests, resendRequests, type RequestKind } from '@/lib/requestsClient';
import { cancelDepositAgreement } from '@/lib/depositAgreementClient';
import { cancelAnimalAddendum } from '@/lib/animalAddendumClient';
import {
  fetchSentItem,
  runItemAction,
  type ConditionReportCorrectionPanel,
  type OwnerContactFormPanel,
  type OwnerIntakeManagement,
  type SentItemDetailResponse,
} from '@/lib/sentItemsClient';

// ---------------------------------------------------------------------------
// Layout
//
// Token-valued inline styles, which is what ThreadDetailPage (the page this one
// replaces) does for exactly this content. The alternative — a stylesheet — has
// to be reached either through `src/index.css` or through W4's `sentItems.css`,
// and neither is this workstream's file. No literal colours or sizes: every
// value is a design-system token or a plain flex rule.
// ---------------------------------------------------------------------------

/** A row of small buttons/chips that wraps. */
const ROW: React.CSSProperties = { display: 'flex', gap: 8, flexWrap: 'wrap' };

/** The submission card's vertical rhythm. */
const CARD_STACK: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-3)',
};

// ---------------------------------------------------------------------------
// Ported from ThreadDetailPage — the sets and copy that decide which lifecycle
// action a document is allowed to offer. Copied verbatim: each one encodes a
// server-side guard's blast radius, and a "tidied" version is a new rule.
// ---------------------------------------------------------------------------

/** Signers in roster order: order group first, then name. */
function sortRoster(signers: SubmissionSigner[]): SubmissionSigner[] {
  return [...signers].sort((a, b) => a.orderGroup - b.orderGroup || a.name.localeCompare(b.name));
}

/**
 * Boundary label for an order group — WHEN its members are invited. Residents
 * and co-signers share the LOWEST group and are invited together at send
 * (2026-08-28: the lead-first gate is gone); records sent before that keep 0/1,
 * so the lowest group is computed, never assumed to be 0 (T4).
 */
function orderGroupLabel(roster: SubmissionSigner[], orderGroup: number): string {
  const lowest = roster.reduce((min, s) => Math.min(min, s.orderGroup), Number.POSITIVE_INFINITY);
  if (orderGroup === lowest) {
    const members = roster.filter((s) => s.orderGroup === orderGroup).length;
    return members > 1 ? 'Invited together at send — sign in any order' : 'Invited at send';
  }
  return 'Invited once everyone above has signed';
}

/** The signer the document is waiting on — the Remind target. */
function nextPendingSigner(sub: SubmissionRow): SubmissionSigner | null {
  return (
    sortRoster(sub.signers).find((s) => {
      const st = s.status.trim().toLowerCase();
      return st !== 'completed' && st !== 'declined';
    }) ?? null
  );
}

// documentType families whose manual resend goes through the unified requests
// dashboard machinery (POST /api/requests/resend with `${kind}:${groupId}:${role}`
// ids — the server's SIGNATURE_KINDS batch path).
const REQUESTS_RESEND_KINDS: ReadonlySet<string> = new Set<RequestKind>([
  'condition_report',
  'moveout_funds_direction',
  'payment_plan',
  'manual_document',
  'property_management_agreement',
]);

const LEASE_DRAFT_DOC_TYPES: ReadonlySet<string> = new Set(['lease', 'adoption_addendum']);

// Signature-backed documentTypes whose staff withdrawal goes through the
// unified requests-dashboard cancel machinery. The group-level lease-draft
// lifecycle endpoints (recall/revise/void) act on ALL of a group's signature
// records across document types, so they are reserved for the lease itself.
//
// WEL-181/182: every signature-backed kind staff can send. Five had a working API
// cancel and no button here (the Owner Contact Addendum also needed the API's
// SIGNATURE_KINDS); the lease is absent on purpose (Recall / Revise / Void) and so
// is the W-9 (no thread, so no submission card — its Cancel is a panel action).
// Mirrors the API's detail.ts set; detail.actions.test.ts pins the two together.
export const CANCEL_REQUEST_DOC_TYPES: ReadonlySet<string> = new Set<RequestKind>([
  'adoption_addendum',
  'payment_plan',
  'manual_document',
  'moveout_funds_direction',
  'animal_addendum_paid',
  'property_management_agreement',
  'condition_report',
  'owner_contact_addendum',
  'leaseholder_change',
  'leaseholder_removal',
  'animal_addendum',
  'city_utility_agreement',
]);

/**
 * Kinds whose cancel is NOT the generic `POST /api/requests/cancel`. Each of these
 * solicited the household through tokenized form links before the document
 * existed, so cancelling is three things — void the agreement, close the links,
 * tell the recipients — and only their own endpoint does all three. The generic
 * path refuses them server-side (DEDICATED_CANCEL_KINDS); this map is what makes
 * the Full Details button do the right thing instead of showing that refusal.
 */
export const DEDICATED_CANCEL: Readonly<Record<string, (groupId: string) => Promise<unknown>>> = {
  moveout_funds_direction: (groupId) => cancelDepositAgreement(groupId),
  animal_addendum_paid: (groupId) => cancelAnimalAddendum(groupId),
};

// The six documentTypes the quiet swap supports (lib/quietSwap.buildReplacement).
// The server owns EVERY guard — this set only keeps the button off documents
// that would always 409 'not_swappable'.
const QUIET_SWAP_DOC_TYPES: ReadonlySet<string> = new Set([
  'lease',
  'payment_plan',
  'property_management_agreement',
  'moveout_funds_direction',
  'manual_document',
  'condition_report',
]);

/** The signature documentType for an Exclusive Property Management Agreement. */
const EPMA_DOC_TYPE = 'property_management_agreement';

/** EPMA state-aware Modify → the builder re-send mode. */
type EpmaModifyMode = 'update' | 'restart-signing' | 'restart-intake';

type LifecycleVerb = 'recall' | 'revise' | 'void' | 'cancel';

const CONFIRM_COPY: Record<
  LifecycleVerb,
  { title: string; consequence: string; confirmLabel: string; tone: 'danger' | 'default' }
> = {
  recall: {
    title: 'Recall this request?',
    consequence:
      'Recall withdraws the request — signer links stop working. The document is kept and can be re-sent unchanged.',
    confirmLabel: 'Recall',
    tone: 'default',
  },
  revise: {
    title: 'Revise this document?',
    consequence:
      "Revise withdraws the current request and reopens the draft for edits. You'll generate and send a new version.",
    confirmLabel: 'Revise',
    tone: 'default',
  },
  void: {
    title: 'Void this document?',
    consequence:
      'This ends signing permanently. The record is kept for audit; nothing remains to sign.',
    confirmLabel: 'Void',
    tone: 'danger',
  },
  cancel: {
    title: 'Cancel this request?',
    consequence:
      'This withdraws the request for every signer. The document record is kept; you can send a new one.',
    confirmLabel: 'Cancel request',
    tone: 'danger',
  },
};

/**
 * The staff DocuSeal console for a submission.
 *
 * `DOCUSEAL_ADMIN_BASE` is a server env var (routes/requestsReview.ts) and the
 * Requests dashboard receives the built URL on its rows. `GET
 * /api/sent-items/:itemId` does not carry one, so this restates the default —
 * which is what prod uses (DocuSeal Cloud).
 *
 * W2: if the detail response ever carries a `docusealUrl` per submission, read
 * it instead of building one here; an overridden base would make this wrong.
 */
const DOCUSEAL_ADMIN_BASE = 'https://docuseal.com';

function mutationError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; message?: string } | null;
    return body?.error ?? body?.message ?? `${fallback} (HTTP ${err.status}).`;
  }
  return err instanceof Error ? err.message : fallback;
}

// ---------------------------------------------------------------------------
// ItemOwnerIntakeBlock — the EPMA's pre-signature management surface.
//
// Ported from ThreadDetailPage's `OwnerIntakeSection` (sol finding 6). That
// page embedded it in the signing spine's empty branch, because an
// owner_agreement thread has NO signature record until an owner completes the
// intake. Under §5.1 that phase is its own row (`owner_intake:<groupId>`), so
// the block belongs on that row's page — which is where it went missing: the
// page was deleted, the row shipped, and the only place to correct an owner's
// email, restart reminders or cancel a stalled intake went with it.
//
// Two things are deliberately different from the original, and nothing else is:
//
//   • The DATA arrives on `GET /api/sent-items/:itemId` (`detail.ownerIntake`)
//     instead of a second `getOwnerIntakeDetail` fetch from inside the
//     component. One page, one payload — a self-fetching block here would be
//     the same duplicate-source-of-truth problem the redesign is fixing
//     elsewhere, and `onChanged` already re-reads the whole item.
//
//   • The staff-prefill/answers half of that old payload is not on this wire.
//     Nothing here rendered it; Modify re-opens the builder, which loads its
//     own seed.
//
// Every MUTATION is the same call with the same arguments the old page made —
// `resendOwnerIntake(groupId)`, `cancelOwnerIntake(groupId, reason?)`,
// `swapOwnerIntakeOwnerEmail(groupId, role, email)` — including the 429
// cooldown and 409 moved-on branches, which are information rather than
// failures and are surfaced as such. Every guard stays server-side.
// ---------------------------------------------------------------------------

const OWNER_TYPE_LABEL: Record<string, string> = {
  individual: 'Individual',
  entity: 'Entity',
};

/** Days-old string off an ISO timestamp ("today" / "1 day" / "N days"). */
function intakeAge(iso: string): string | null {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  return formatAge(Math.max(0, (Date.now() - then) / 86_400_000));
}

/** The dormant-feature message, said once. `EPMA_SEND_ENABLED` /
 *  `WHP_OWN_MESSAGING` off means every control here 503s. */
const OWNER_INTAKE_DORMANT =
  "Owner-intake management isn't enabled on the API right now (EPMA_SEND_ENABLED), so this intake can't be managed here.";

export function ItemOwnerIntakeBlock({
  intake,
  onChanged,
}: {
  intake: OwnerIntakeManagement;
  /** The intake moved — re-read the item. */
  onChanged: () => void;
}): React.JSX.Element {
  const [busy, setBusy] = useState<null | 'resend' | 'cancel'>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const groupId = intake.groupId;
  const cancelled = intake.status === 'cancelled' || !!intake.cancelledAt;
  const awaiting =
    !cancelled && (intake.status === 'intake_sent' || intake.status === 'in_progress');
  const processing =
    !cancelled && (intake.status === 'generating' || intake.status === 'ready_to_sign');

  const age = intakeAge(intake.createdAt);
  const headerChip = cancelled
    ? { tone: 'closed' as const, label: 'Cancelled' }
    : processing
      ? {
          tone: 'active' as const,
          label: intake.status === 'generating' ? 'Generating agreement' : 'Ready to sign',
        }
      : { tone: 'active' as const, label: 'Awaiting owner intake' };

  const restartReminders = async () => {
    setBusy('resend');
    try {
      const res = await resendOwnerIntake(groupId);
      toast.success(
        `Reminder sent to ${res.ownersEmailed} ${
          res.ownersEmailed === 1 ? 'owner' : 'owners'
        } — the reminder clock restarted.`,
      );
      onChanged();
    } catch (err) {
      if (err instanceof OwnerIntakeNotEnabledError) {
        toast.info(OWNER_INTAKE_DORMANT);
      } else if (err instanceof ApiError && err.status === 429) {
        // Cooldown, not a failure — surface the server's message as-is.
        const msg =
          (err.body as { error?: string } | null)?.error ??
          'A reminder just went out — please wait before resending.';
        toast.info(msg, { title: 'Reminder cooldown' });
      } else if (err instanceof ApiError && err.status === 409) {
        toast.info(mutationError(err, 'This intake has moved on.'));
        onChanged();
      } else {
        toast.error(mutationError(err, "Couldn't send the reminder"));
      }
    } finally {
      setBusy(null);
    }
  };

  const runCancel = async () => {
    setBusy('cancel');
    try {
      const reason = cancelReason.trim();
      await cancelOwnerIntake(groupId, reason ? reason : undefined);
      toast.success('Owner intake cancelled — the owner links no longer work.');
      setConfirmCancel(false);
      setCancelReason('');
      onChanged();
    } catch (err) {
      if (err instanceof OwnerIntakeNotEnabledError) {
        toast.info(OWNER_INTAKE_DORMANT);
      } else {
        toast.error(mutationError(err, "Couldn't cancel the intake"));
      }
      if (err instanceof ApiError && err.status === 409) {
        setConfirmCancel(false);
        onChanged();
      }
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="oc-panel" data-section="owner-intake">
      <div className="oc-kicker">Owner intake — the pre-signature phase</div>
      <div className="card" style={CARD_STACK}>
        {/* What this is · state · sent/age */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 'var(--weight-medium)' }}>Owner intake</span>
          <StatusChip tone={headerChip.tone}>{headerChip.label}</StatusChip>
          {intake.round > 1 && <span className="oc-meta">round {intake.round}</span>}
          <span
            className="oc-meta"
            style={{ marginLeft: 'auto', fontVariantNumeric: 'var(--numerals-tabular)' }}
          >
            sent {formatWhen(intake.createdAt)}
            {age ? ` · ${age === 'today' ? 'today' : `${age} ago`}` : ''}
          </span>
        </div>

        {/* State explainer */}
        {awaiting && (
          <div className="oc-meta">
            The owners still owe the intake questionnaire. Nothing exists to sign yet — the
            agreement is generated and sent for signature automatically as soon as the first
            owner completes the form.
          </div>
        )}
        {processing && (
          <div className="oc-meta">
            {intake.status === 'generating'
              ? 'The intake is complete — the agreement is being generated and sent for signature. Refresh in a moment to see the signing status.'
              : 'The agreement is out for signature — refresh to see the signing status.'}
          </div>
        )}
        {cancelled && (
          <div className="oc-meta">
            Cancelled {intake.cancelledAt ? formatWhen(intake.cancelledAt) : ''}
            {intake.cancelledByDisplay ? ` by ${intake.cancelledByDisplay}` : ''} — the owner
            links no longer work and reminders stopped. The record is kept for audit.
            {intake.cancelledReason && (
              <span style={{ display: 'block', marginTop: 4 }}>
                Reason: &ldquo;{intake.cancelledReason}&rdquo;
              </span>
            )}
          </div>
        )}

        {/* Owners */}
        <div>
          {intake.owners.map((o, i) => (
            <div key={o.role} className="oc-signer" style={{ flexWrap: 'wrap' }}>
              <span className="oc-signer-order">{i + 1}</span>
              <span className="oc-signer-name">
                {o.name}
                <span className="oc-meta" style={{ display: 'block', overflowWrap: 'anywhere' }}>
                  {o.email || 'no email'}
                </span>
              </span>
              <span className="oc-signer-role">
                {OWNER_TYPE_LABEL[o.ownerType] ?? o.ownerType}
              </span>
              <span className="oc-signer-state">
                <StatusChip tone={o.completed ? 'done' : cancelled ? 'closed' : 'active'}>
                  {o.completed ? 'Completed' : cancelled ? 'Not completed' : 'Awaiting'}
                </StatusChip>
              </span>
              {awaiting && !o.completed && (
                <SignerEmailSwap
                  inputId={`oi-swap-${groupId}-${o.role}`}
                  signerName={o.name}
                  currentEmail={o.email}
                  disabled={busy !== null}
                  onSubmit={async (email) => {
                    const res = await swapOwnerIntakeOwnerEmail(groupId, o.role, email);
                    toast.success(
                      res.reinvited
                        ? `Email updated — ${o.name} was re-invited at ${res.owner.email}.`
                        : `Email updated to ${res.owner.email}, but the re-invite didn't send — use Restart reminders.`,
                    );
                  }}
                  onSaved={onChanged}
                />
              )}
            </div>
          ))}
        </div>

        {/* Reminders */}
        {!cancelled && (
          <div className="oc-meta" style={{ fontVariantNumeric: 'var(--numerals-tabular)' }}>
            {intake.reminders.count > 0
              ? `Reminders: ${intake.reminders.count} sent${
                  intake.reminders.lastReminderAt
                    ? ` · last ${formatWhen(intake.reminders.lastReminderAt)}`
                    : ''
                }`
              : 'Reminders: none sent yet.'}
          </div>
        )}

        {/* Actions — only while the intake is still awaiting the owner */}
        {awaiting && (
          <div style={ROW}>
            <button
              type="button"
              className="btn"
              data-variant="secondary"
              data-size="sm"
              disabled={busy !== null}
              onClick={() => void restartReminders()}
            >
              {busy === 'resend' ? 'Sending…' : 'Restart reminders'}
            </button>
            <Link
              className="btn"
              data-variant="secondary"
              data-size="sm"
              to={`/send/property-management-agreement?from=${encodeURIComponent(groupId)}`}
              title="Re-open the builder pre-loaded with this engagement"
            >
              Modify
            </Link>
            <button
              type="button"
              className="btn"
              data-variant="danger-ghost"
              data-size="sm"
              disabled={busy !== null}
              onClick={() => {
                setCancelReason('');
                setConfirmCancel(true);
              }}
            >
              Cancel intake
            </button>
          </div>
        )}
        {processing && (
          <div style={ROW}>
            <button
              type="button"
              className="btn"
              data-variant="secondary"
              data-size="sm"
              onClick={onChanged}
            >
              Refresh
            </button>
          </div>
        )}

        <ConsequenceConfirm
          open={confirmCancel}
          title="Cancel this owner intake?"
          consequence={
            <>
              The owner form closes and reminders stop — the owners can no longer complete the
              intake, and no agreement will be generated. The record is kept for audit; nothing
              is emailed to the owners.
              <span className="field" style={{ display: 'block', marginTop: 'var(--space-3)' }}>
                <label htmlFor="oi-cancel-reason" className="label">
                  Reason (optional)
                </label>
                <textarea
                  id="oi-cancel-reason"
                  className="input"
                  rows={2}
                  value={cancelReason}
                  disabled={busy === 'cancel'}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Shown on the cancelled record."
                />
              </span>
            </>
          }
          confirmLabel="Cancel intake"
          tone="danger"
          busy={busy === 'cancel'}
          onConfirm={() => void runCancel()}
          onCancel={() => {
            if (busy !== 'cancel') setConfirmCancel(false);
          }}
        />
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// ItemOwnerContactFormBlock — the Owner Contact form: out for the owners, or cancelled.
//
// The form that follows an executed agreement has no item of its own until an
// owner completes it and the addendum is generated, so a request staff (or the
// chain) sent had nowhere to be cancelled from (WEL-181/182). It rides the
// agreement's page instead. While it is OUT it offers Cancel; once cancelled the
// block STAYS — "Cancelled — {date} by {name}" with "Send the form again" — so a
// cancel is visible and undoable, never a dead end. Every control is the server's
// own `ItemAction`, run through `runItemAction` (which turns a 200-that-failed
// into the server's reason); this block builds no URL. Names and dates only.
// ---------------------------------------------------------------------------

export function ItemOwnerContactFormBlock({
  form,
  onChanged,
}: {
  form: OwnerContactFormPanel;
  /** The form moved — re-read the item. */
  onChanged: () => void;
}): React.JSX.Element {
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<'cancel' | 'send-again' | null>(null);

  const cancelled = form.status === 'cancelled';
  const action = confirm === 'send-again' ? form.sendAgain : form.cancel;

  const run = async () => {
    if (!action) return;
    setBusy(true);
    try {
      await runItemAction(action);
      toast.success(
        confirm === 'send-again'
          ? 'Owner contact form sent again.'
          : "Owner contact form cancelled — the owners' links no longer work.",
      );
      setConfirm(null);
      onChanged();
    } catch (err) {
      toast.error(
        mutationError(
          err,
          confirm === 'send-again'
            ? "Couldn't send the owner contact form again"
            : "Couldn't cancel the owner contact form",
        ),
      );
      // A 409 means the form moved first (an owner finished it): the page is stale, not the click wrong.
      if (err instanceof ApiError && err.status === 409) {
        setConfirm(null);
        onChanged();
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="oc-panel" data-section="owner-contact-form">
      <div className="oc-kicker">
        {cancelled ? 'Owner contact form — cancelled' : 'Owner contact form — out for the owners'}
      </div>
      <div className="card" style={CARD_STACK}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 'var(--weight-medium)' }}>Owner contact form</span>
          <StatusChip tone={cancelled ? 'closed' : 'active'}>
            {cancelled ? 'Cancelled' : form.status === 'in_progress' ? 'Started' : 'Awaiting owners'}
          </StatusChip>
          <span
            className="oc-meta"
            style={{ marginLeft: 'auto', fontVariantNumeric: 'var(--numerals-tabular)' }}
          >
            sent {formatWhen(form.sentAt)}
          </span>
        </div>
        {cancelled ? (
          <div className="oc-meta">
            Cancelled{form.cancelledAt ? ` ${formatWhen(form.cancelledAt)}` : ''}
            {form.cancelledBy ? ` by ${form.cancelledBy}` : ''} — the owners&rsquo; links show that
            it was cancelled, and the agreement itself is not affected. It was sent to{' '}
            {form.sentTo}.
          </div>
        ) : (
          <div className="oc-meta">
            Sent to {form.sentTo}. Once an owner completes it the contact addendum is generated and
            sent for signature.
          </div>
        )}
        <div style={ROW}>
          {form.cancel && (
            <button
              type="button"
              className="btn"
              data-variant="danger-ghost"
              data-size="sm"
              disabled={busy}
              onClick={() => setConfirm('cancel')}
            >
              {form.cancel.label}
            </button>
          )}
          {form.sendAgain && (
            <button
              type="button"
              className="btn"
              data-variant="secondary"
              data-size="sm"
              disabled={busy}
              onClick={() => setConfirm('send-again')}
            >
              {form.sendAgain.label}
            </button>
          )}
        </div>
        <ConsequenceConfirm
          open={confirm !== null}
          title={
            confirm === 'send-again' ? 'Send the owner contact form again?' : 'Cancel the owner contact form?'
          }
          consequence={action?.confirm ?? ''}
          confirmLabel={action?.label ?? 'Continue'}
          tone={confirm === 'send-again' ? 'default' : 'danger'}
          busy={busy}
          onConfirm={() => void run()}
          onCancel={() => {
            if (!busy) setConfirm(null);
          }}
        />
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// ConditionReportCorrectionBlock — "Request new signatures" (2026-10-06).
//
// A condition report that went out wrong (WEL-175: without its photos) after a
// resident already signed it. The quiet swap refuses that case — it would void a
// signature — so this sends a CORRECTED copy alongside the first: it replaces the
// first only once every resident signs it; until then, or if it is withdrawn,
// the first stands. The server owns every guard; this block offers the two
// actions the payload says are open and states where the correction is.
// ---------------------------------------------------------------------------

export function ConditionReportCorrectionBlock({
  panel,
  onChanged,
}: {
  panel: ConditionReportCorrectionPanel;
  /** The report moved — re-read the item. */
  onChanged: () => void;
}): React.JSX.Element | null {
  const [busy, setBusy] = useState<null | 'request' | 'withdraw' | 'resend' | 'resolve'>(null);
  const [confirm, setConfirm] = useState<null | 'request' | 'withdraw'>(null);
  const [whatChanged, setWhatChanged] = useState('');

  const pending = panel.state === 'pending';
  const tookEffect = panel.state === 'took_effect';
  const unknown = panel.state === 'withdraw_unknown';
  if (!pending && !tookEffect && !unknown && !panel.canRequest && !panel.canFinish) return null;

  // A withdrawal DocuSeal couldn't confirm: staff checked DocuSeal — record what
  // they found. Both actions are guarded server-side.
  const runResolve = async (outcome: 'withdrawn' | 'pending') => {
    setBusy('resolve');
    try {
      await resolveConditionReportWithdrawal(panel.resolveWithdrawalEndpoint, outcome);
      toast.success(outcome === 'withdrawn' ? CORRECTION_COPY.withdrawnToast : CORRECTION_COPY.putBackToast);
      onChanged();
    } catch (err) {
      toast.error(mutationError(err, "That couldn't be recorded"));
      if (err instanceof ApiError && (err.status === 404 || err.status === 409)) onChanged();
    } finally {
      setBusy(null);
    }
  };

  const runRequest = async () => {
    setBusy('request');
    try {
      const trimmed = whatChanged.trim();
      const res = await requestConditionReportNewSignatures(panel.requestEndpoint, {
        ...(trimmed ? { whatChanged: trimmed } : {}),
      });
      toast.success(correctionRequestedToast(res.emailed));
      setConfirm(null);
      setWhatChanged('');
      onChanged();
    } catch (err) {
      toast.error(mutationError(err, "The corrected version wasn't sent"));
      if (err instanceof ApiError && (err.status === 404 || err.status === 409)) {
        setConfirm(null);
        onChanged();
      }
    } finally {
      setBusy(null);
    }
  };

  // Finish an unfinished correction — invites SES has not accepted yet, or a
  // corrected version created in DocuSeal but never recorded: the request
  // endpoint's reconcile finishes it (never a second copy).
  const runResend = async () => {
    setBusy('resend');
    try {
      await requestConditionReportNewSignatures(panel.requestEndpoint, {});
      toast.success(CORRECTION_COPY.resentToast);
      onChanged();
    } catch (err) {
      toast.error(mutationError(err, "The corrected version wasn't re-sent"));
      if (err instanceof ApiError && (err.status === 404 || err.status === 409)) onChanged();
    } finally {
      setBusy(null);
    }
  };

  const runWithdraw = async () => {
    setBusy('withdraw');
    try {
      await withdrawConditionReportCorrection(panel.withdrawEndpoint);
      toast.success(CORRECTION_COPY.withdrawnToast);
      setConfirm(null);
      onChanged();
    } catch (err) {
      toast.error(mutationError(err, "The corrected version wasn't withdrawn"));
      if (err instanceof ApiError && (err.status === 404 || err.status === 409)) {
        setConfirm(null);
        onChanged();
      }
    } finally {
      setBusy(null);
    }
  };

  const requested = pending ? correctionRequestedLine(panel) : '';
  const told = pending ? correctionWhatChangedLine(panel) : '';

  return (
    <section className="oc-panel" data-section="cr-correction">
      <div className="oc-kicker">Corrected version</div>
      <div className="card" style={CARD_STACK}>
        {pending && (
          <>
            <div role="status">{CORRECTION_COPY.pendingLine}</div>
            {requested && <div className="oc-meta">{requested}</div>}
            {told && <div className="oc-meta">{told}</div>}
          </>
        )}
        {panel.canFinish && <div className="oc-meta">{CORRECTION_COPY.finishHint}</div>}
        {unknown && <div role="status">{CORRECTION_COPY.unknownLine}</div>}
        {tookEffect && <div className="oc-meta">{correctionTookEffectLine(panel)}</div>}
        <div style={ROW}>
          {panel.canRequest && (
            <button
              type="button"
              className="btn"
              data-variant="secondary"
              data-size="sm"
              disabled={busy !== null}
              onClick={() => {
                setWhatChanged('');
                setConfirm('request');
              }}
            >
              {busy === 'request' ? 'Sending…' : CORRECTION_COPY.requestButton}
            </button>
          )}
          {panel.canFinish && (
            <button
              type="button"
              className="btn"
              data-variant="secondary"
              data-size="sm"
              disabled={busy !== null}
              onClick={() => void runResend()}
            >
              {busy === 'resend' ? 'Sending…' : CORRECTION_COPY.finishButton}
            </button>
          )}
          {unknown && (
            <>
              <button
                type="button"
                className="btn"
                data-variant="secondary"
                data-size="sm"
                disabled={busy !== null}
                onClick={() => void runResolve('withdrawn')}
              >
                {CORRECTION_COPY.markWithdrawnButton}
              </button>
              <button
                type="button"
                className="btn"
                data-variant="secondary"
                data-size="sm"
                disabled={busy !== null}
                onClick={() => void runResolve('pending')}
              >
                {CORRECTION_COPY.putBackButton}
              </button>
            </>
          )}
          {pending && (
            <button
              type="button"
              className="btn"
              data-variant="danger-ghost"
              data-size="sm"
              disabled={busy !== null}
              onClick={() => setConfirm('withdraw')}
            >
              {busy === 'withdraw' ? 'Withdrawing…' : CORRECTION_COPY.withdrawButton}
            </button>
          )}
        </div>

        <ConsequenceConfirm
          open={confirm === 'request'}
          title={CORRECTION_COPY.requestTitle}
          consequence={
            <>
              {CORRECTION_COPY.requestConsequence}
              <span className="field" style={{ display: 'block', marginTop: 'var(--space-3)' }}>
                <label htmlFor="cr-correction-what-changed" className="label">
                  {CORRECTION_COPY.whatChangedLabel}
                </label>
                <textarea
                  id="cr-correction-what-changed"
                  className="input"
                  rows={2}
                  maxLength={WHAT_CHANGED_MAX}
                  value={whatChanged}
                  disabled={busy === 'request'}
                  onChange={(e) => setWhatChanged(e.target.value)}
                  placeholder={CORRECTION_COPY.whatChangedPlaceholder}
                />
              </span>
            </>
          }
          confirmLabel={CORRECTION_COPY.requestConfirm}
          tone="default"
          busy={busy === 'request'}
          onConfirm={() => void runRequest()}
          onCancel={() => {
            if (busy !== 'request') setConfirm(null);
          }}
        />
        <ConsequenceConfirm
          open={confirm === 'withdraw'}
          title={CORRECTION_COPY.withdrawTitle}
          consequence={CORRECTION_COPY.withdrawConsequence}
          confirmLabel={CORRECTION_COPY.withdrawConfirm}
          tone="danger"
          busy={busy === 'withdraw'}
          onConfirm={() => void runWithdraw()}
          onCancel={() => {
            if (busy !== 'withdraw') setConfirm(null);
          }}
        />
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// ItemSubmissionCard — one trip through the signing spine.
//
// Ported from ThreadDetailPage's SubmissionCard. Every client call below is the
// call that page makes, with the same arguments: resendRequests / cancelRequests
// with `${documentType}:${groupId}:${role}` ids, voidLeaseVersion(groupId,
// versionId) for Recall, reviseLease(groupId, 'cancel' | 'cancel-resend') for
// Void / Revise, quietSwapThread(threadId, {submissionId, reason}),
// updateSignerEmail(groupId, role, email, documentType), and the two presign
// download endpoints. The only additions are the DocuSeal link and the
// `viewable` gate on the downloads (§6.5, T6).
// ---------------------------------------------------------------------------

function ItemSubmissionCard({
  submission,
  threadId,
  leaseLifecycle,
  newest,
  newestOfType,
  viewable,
  onChanged,
  onRevised,
  onModifyEpma,
}: {
  submission: SubmissionRow;
  /** The quiet-swap endpoint is thread-addressed; the submission carries its
   *  own thread id (snapshot.ts sets it), so the page needs no thread fetch. */
  threadId: string | null;
  /** This item IS the lease workflow — the only one whose lease document may
   *  use the group-level Recall/Revise/Void (they act on the whole group). */
  leaseLifecycle: boolean;
  /** Newest submission for the item — the only card that may offer downloads
   *  (the presign endpoints serve only the newest record). */
  newest: boolean;
  /** Newest submission for its documentType — the quiet-swap staleness pin is
   *  per-type server-side, so only this card may offer the swap. */
  newestOfType: boolean;
  /** The ROW's `viewable`. False for a W-9 (TIN) and anything the registry
   *  marks unviewable: no signed PDF, no audit certificate, no DocuSeal link
   *  — a download must never be reachable by a path that only checks whether a
   *  file exists (T6). */
  viewable: boolean;
  onChanged: () => void;
  onRevised: () => void;
  onModifyEpma: (groupId: string, mode: EpmaModifyMode) => void;
}): React.JSX.Element {
  const [busy, setBusy] = useState<
    null | 'remind' | 'lifecycle' | 'swap' | 'signed-pdf' | 'audit-cert'
  >(null);
  const [confirm, setConfirm] = useState<LifecycleVerb | null>(null);
  const [confirmSwap, setConfirmSwap] = useState(false);
  const [swapReason, setSwapReason] = useState('');
  const [modifyPhase, setModifyPhase] = useState<null | 'partial'>(null);

  const open = isSubmissionOpen(submission.status) && !submission.supersededBy;
  const roster = sortRoster(submission.signers);
  const remindTarget = open ? nextPendingSigner(submission) : null;
  const canRemind =
    remindTarget !== null &&
    (LEASE_DRAFT_DOC_TYPES.has(submission.documentType) ||
      REQUESTS_RESEND_KINDS.has(submission.documentType));
  const canLifecycle = leaseLifecycle && submission.documentType === 'lease';
  const firstSignerRole = roster[0]?.role ?? null;
  // A pending corrected condition report is withdrawn from its own block — the
  // generic Cancel's "for every signer" copy would misdescribe it.
  const pendingCorrection = submission.correction === 'pending';
  const canCancel =
    CANCEL_REQUEST_DOC_TYPES.has(submission.documentType) &&
    firstSignerRole !== null &&
    !pendingCorrection;

  const completedCount = roster.filter((s) => s.status.trim().toLowerCase() === 'completed').length;
  const isFullyExecuted = roster.length > 0 && completedCount === roster.length;
  // An EPMA must stay modifiable UNTIL it is fully executed — including a
  // cancelled/voided one, which is recovered by regenerating a fresh version.
  const canModifyEpma =
    submission.documentType === EPMA_DOC_TYPE && firstSignerRole !== null && !isFullyExecuted;
  const canQuietSwap =
    newestOfType &&
    open &&
    QUIET_SWAP_DOC_TYPES.has(submission.documentType) &&
    submission.docusealSubmissionId !== null &&
    completedCount === 0 &&
    submission.signedCount === 0 &&
    !pendingCorrection &&
    threadId !== null;

  const remind = async () => {
    if (!remindTarget) return;
    setBusy('remind');
    try {
      // Always the requests-dashboard resend: it is DOC-TYPE-SCOPED server-side,
      // unlike the lease-drafts per-signer resend which resolves the group's
      // newest record across ALL document types.
      const id = `${submission.documentType}:${submission.groupId}:${remindTarget.role}`;
      const res = await resendRequests([id]);
      if (res.failed > 0) {
        const reason = res.results[0]?.reason;
        throw new Error(
          reason ? `Couldn't send the reminder: ${reason}` : "Couldn't send the reminder.",
        );
      }
      toast.success(`Reminder sent to ${remindTarget.name}.`);
      onChanged();
    } catch (err) {
      toast.error(mutationError(err, "Couldn't send the reminder"));
    } finally {
      setBusy(null);
    }
  };

  const runLifecycle = async () => {
    if (!confirm) return;
    const verb = confirm;
    setBusy('lifecycle');
    try {
      if (verb === 'cancel') {
        if (!firstSignerRole) throw new Error('No signer on this request.');
        const dedicated = DEDICATED_CANCEL[submission.documentType];
        if (dedicated) {
          await dedicated(submission.groupId);
        } else {
          const id = `${submission.documentType}:${submission.groupId}:${firstSignerRole}`;
          const res = await cancelRequests([id], { notify: false });
          if (res.failed > 0) {
            const reason = res.results[0]?.reason;
            throw new Error(
              reason ? `Couldn't cancel the request: ${reason}` : "Couldn't cancel the request.",
            );
          }
        }
        toast.success(
          dedicated
            ? 'Request cancelled. The household has been told and their form links are closed.'
            : 'Request cancelled. The document record is kept.',
        );
        setConfirm(null);
        onChanged();
      } else if (verb === 'recall') {
        await voidLeaseVersion(submission.groupId, submission.versionId);
        toast.success('Request recalled. The document is kept and can be re-sent.');
        setConfirm(null);
        onChanged();
      } else if (verb === 'void') {
        await reviseLease(submission.groupId, 'cancel');
        toast.success('Document voided. Nothing remains to sign.');
        setConfirm(null);
        onChanged();
      } else {
        await reviseLease(submission.groupId, 'cancel-resend');
        toast.success('Request withdrawn — the draft is open for edits.');
        setConfirm(null);
        onRevised();
      }
    } catch (err) {
      toast.error(mutationError(err, 'Action failed'));
    } finally {
      setBusy(null);
    }
  };

  // Quiet swap: silently replace the out-for-signature submission with a freshly
  // produced current version. Signers keep their links + reminder schedule —
  // nobody is emailed. The server owns every guard and refuses with a 409 + a
  // plain-language reason; a refusal means our on-screen state is stale, so we
  // reload rather than argue.
  const runSwap = async () => {
    if (submission.docusealSubmissionId === null || !threadId) return;
    setBusy('swap');
    try {
      const reason = swapReason.trim();
      const res = await quietSwapThread(threadId, {
        submissionId: submission.docusealSubmissionId,
        ...(reason ? { reason } : {}),
      });
      setConfirmSwap(false);
      setSwapReason('');
      toast.success(
        'Document swapped — signers keep their links and reminder schedule; nobody was notified.',
      );
      if (res.warning) toast.warning(res.warning, { title: 'Swap done — cleanup needed' });
      onChanged();
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string; code?: string } | null;
        toast.error(body?.error ?? `The swap was not performed (HTTP ${err.status}).`, {
          title: 'Swap refused',
        });
        if (err.status === 404 || err.status === 409) {
          setConfirmSwap(false);
          onChanged();
        }
      } else {
        toast.error(mutationError(err, 'The swap was not performed'));
      }
    } finally {
      setBusy(null);
    }
  };

  // EPMA Modify: the click TOUCHES NOTHING — it opens the builder pre-loaded in
  // the chosen mode. The current agreement stays LIVE; the withdrawal is
  // server-side and atomic with the Send.
  const goModify = (mode: EpmaModifyMode) => {
    setModifyPhase(null);
    onModifyEpma(submission.groupId, mode);
  };

  const download = async (which: 'signed-pdf' | 'audit-cert') => {
    setBusy(which);
    try {
      const { url } =
        which === 'signed-pdf'
          ? await getSignedPdfUrl(submission.groupId)
          : await getAuditCertUrl(submission.groupId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast.error(mutationError(err, "Couldn't prepare the download"));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="card" data-submission={submission.submissionKey} style={CARD_STACK}>
      {/* Header: document · status · sent/progress · superseded */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 'var(--weight-medium)' }}>{submission.documentLabel}</span>
        <StatusChip tone={submissionTone(submission.status)}>{submission.statusLabel}</StatusChip>
        <span
          className="oc-meta"
          style={{ marginLeft: 'auto', fontVariantNumeric: 'var(--numerals-tabular)' }}
        >
          sent {formatWhen(submission.createdAt)} · {submission.signedCount}/{submission.totalCount}{' '}
          signed
        </span>
        {submission.supersededBy && <span className="oc-meta">Superseded</span>}
        {submission.correction && <span className="tag">Corrected version</span>}
        {viewable && submission.docusealSubmissionId !== null && (
          <a
            className="btn"
            data-variant="ghost"
            data-size="sm"
            href={`${DOCUSEAL_ADMIN_BASE}/submissions/${submission.docusealSubmissionId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            View in DocuSeal ↗
          </a>
        )}
      </div>

      {/* Roster: order groups, boundaries stated */}
      <div>
        {roster.map((s, i) => {
          const prev = i > 0 ? roster[i - 1] : undefined;
          const boundary = prev === undefined || prev.orderGroup !== s.orderGroup;
          // Change email: any signer who hasn't signed/declined, on the NEWEST
          // open submission only — the PATCH targets the group's newest record
          // for this documentType, so older cards must not offer it.
          const st = s.status.trim().toLowerCase();
          const canSwapEmail = newest && open && st !== 'completed' && st !== 'declined';
          return (
            <div key={`${s.orderGroup}:${s.role}:${s.name}`}>
              {boundary && (
                <div className="oc-meta" style={{ padding: '6px 0 2px' }}>
                  {orderGroupLabel(roster, s.orderGroup)}
                </div>
              )}
              <div className="oc-signer" style={{ flexWrap: 'wrap' }}>
                <span className="oc-signer-order">{s.orderGroup}</span>
                <span className="oc-signer-name">
                  {s.name}
                  {s.email && (
                    <span className="oc-meta" style={{ display: 'block', overflowWrap: 'anywhere' }}>
                      {s.email}
                    </span>
                  )}
                </span>
                <span className="oc-signer-role">
                  {s.role}
                  {s.applicantType ? ` · ${s.applicantType}` : ''}
                </span>
                <span className="oc-signer-state">
                  <StatusChip tone={signerTone(s.status)}>
                    {s.status}
                    {s.completedAt
                      ? ` ${formatWhen(s.completedAt)}`
                      : s.openedAt
                        ? ` · opened ${formatWhen(s.openedAt)}`
                        : s.sentAt
                          ? ` · sent ${formatWhen(s.sentAt)}`
                          : ''}
                  </StatusChip>
                </span>
                {canSwapEmail && (
                  <SignerEmailSwap
                    inputId={`swap-${submission.submissionKey}-${s.role}`}
                    signerName={s.name}
                    currentEmail={s.email ?? ''}
                    disabled={busy !== null}
                    onSubmit={async (email) => {
                      // Doc-type-scoped so the swap hits THIS record (a group
                      // can hold both a lease and an adoption addendum).
                      await updateSignerEmail(
                        submission.groupId,
                        s.role,
                        email,
                        submission.documentType as CurrentDocumentType,
                      );
                      toast.success(`Email updated — ${s.name} was re-invited at ${email}.`);
                    }}
                    onSaved={onChanged}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Actions — in flight only; but EPMA Modify survives a Cancelled/Voided
          submission (recover via regenerate). */}
      {((open && (canRemind || canLifecycle || canCancel || canQuietSwap)) || canModifyEpma) && (
        <div style={ROW}>
          {canRemind && remindTarget && (
            <button
              type="button"
              className="btn"
              data-variant="secondary"
              data-size="sm"
              disabled={busy !== null}
              onClick={() => void remind()}
            >
              {busy === 'remind' ? 'Sending…' : `Remind ${remindTarget.name}`}
            </button>
          )}
          {canLifecycle && (
            <>
              <button
                type="button"
                className="btn"
                data-variant="secondary"
                data-size="sm"
                disabled={busy !== null}
                onClick={() => setConfirm('recall')}
              >
                Recall
              </button>
              <button
                type="button"
                className="btn"
                data-variant="secondary"
                data-size="sm"
                disabled={busy !== null}
                onClick={() => setConfirm('revise')}
              >
                Revise
              </button>
              <button
                type="button"
                className="btn"
                data-variant="danger-ghost"
                data-size="sm"
                disabled={busy !== null}
                onClick={() => setConfirm('void')}
              >
                Void
              </button>
            </>
          )}
          {canQuietSwap && (
            <button
              type="button"
              className="btn"
              data-variant="secondary"
              data-size="sm"
              disabled={busy !== null}
              onClick={() => {
                setSwapReason('');
                setConfirmSwap(true);
              }}
              title="Silently replace this document with a freshly produced version — signer links and reminders continue unchanged"
            >
              Swap for revised version
            </button>
          )}
          {canModifyEpma && (
            <button
              type="button"
              className="btn"
              data-variant="secondary"
              data-size="sm"
              disabled={busy !== null}
              onClick={() =>
                // A cancelled/voided (not-open) EPMA has no live signatures →
                // regenerate; only a LIVE partially-signed one offers the
                // restart-signing / restart-intake choice.
                !open || completedCount === 0 ? goModify('update') : setModifyPhase('partial')
              }
              title="Revise this agreement — the current version stays live until you send"
            >
              Modify
            </button>
          )}
          {canCancel && (
            <button
              type="button"
              className="btn"
              data-variant="danger-ghost"
              data-size="sm"
              disabled={busy !== null}
              onClick={() => setConfirm('cancel')}
            >
              Cancel request
            </button>
          )}
        </div>
      )}

      {/* Downloads — the presign endpoints serve only the group's NEWEST record,
          so only the newest, fully-executed card offers them. Gated on the row's
          `viewable` as well: a W-9's signed PDF carries the owner's TIN (T6). */}
      {viewable &&
        newest &&
        submission.status === 'Fully Executed' &&
        (submission.hasSignedPdf || submission.hasAuditCert) && (
          <div style={ROW}>
            {submission.hasSignedPdf && (
              <button
                type="button"
                className="btn"
                data-variant="ghost"
                data-size="sm"
                disabled={busy !== null}
                onClick={() => void download('signed-pdf')}
              >
                {busy === 'signed-pdf' ? 'Preparing…' : 'Download signed PDF'}
              </button>
            )}
            {submission.hasAuditCert && (
              <button
                type="button"
                className="btn"
                data-variant="ghost"
                data-size="sm"
                disabled={busy !== null}
                onClick={() => void download('audit-cert')}
              >
                {busy === 'audit-cert' ? 'Preparing…' : 'Audit certificate'}
              </button>
            )}
          </div>
        )}

      <ConsequenceConfirm
        open={confirm !== null}
        title={confirm ? CONFIRM_COPY[confirm].title : ''}
        consequence={confirm ? CONFIRM_COPY[confirm].consequence : ''}
        confirmLabel={confirm ? CONFIRM_COPY[confirm].confirmLabel : 'Confirm'}
        tone={confirm ? CONFIRM_COPY[confirm].tone : 'default'}
        busy={busy === 'lifecycle'}
        onConfirm={() => void runLifecycle()}
        onCancel={() => {
          if (busy !== 'lifecycle') setConfirm(null);
        }}
      />

      {/* Quiet swap — names the document and states the zero-signature rule. */}
      <ConsequenceConfirm
        open={confirmSwap}
        title="Swap for revised version?"
        consequence={
          <>
            This quietly replaces the <b>{submission.documentLabel}</b> that is out for signature
            with a freshly produced current version. Signers are not notified — their signing links
            keep working and the reminder schedule continues unchanged. Allowed only while{' '}
            <b>zero</b> signers have completed; if anyone has already signed (or signs while the
            swap is in flight), the server refuses and nothing changes for signers.
            <span className="field" style={{ display: 'block', marginTop: 'var(--space-3)' }}>
              <label htmlFor={`swap-reason-${submission.submissionKey}`} className="label">
                Reason (optional)
              </label>
              <textarea
                id={`swap-reason-${submission.submissionKey}`}
                className="input"
                rows={2}
                value={swapReason}
                disabled={busy === 'swap'}
                onChange={(e) => setSwapReason(e.target.value)}
                placeholder="Recorded in the activity log."
              />
            </span>
          </>
        }
        confirmLabel="Swap document"
        tone="default"
        busy={busy === 'swap'}
        onConfirm={() => void runSwap()}
        onCancel={() => {
          if (busy !== 'swap') setConfirmSwap(false);
        }}
      />

      {/* EPMA Modify — partially signed: pick how to continue. A MODE PICKER,
          not a withdrawal; nothing is touched until you Send in the builder. */}
      <ChoiceDialog
        open={modifyPhase === 'partial'}
        title="This agreement is partially signed"
        intro="You can revise it — the current version stays live until you send the new one. Choose how to continue:"
        onCancel={() => setModifyPhase(null)}
        options={[
          {
            key: 'restart-signing',
            label: 'Restart signing',
            description:
              'Send the updated agreement for signature — everyone signs the new version. Keeps the owners’ current answers.',
            onSelect: () => goModify('restart-signing'),
          },
          {
            key: 'restart-intake',
            label: 'Restart intake',
            description:
              'Re-collect the owners’ information from scratch, then the agreement goes back out to sign.',
            onSelect: () => goModify('restart-intake'),
          },
        ]}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// The page
// ---------------------------------------------------------------------------

/**
 * The quiet-swap staleness pin is per documentType and is chosen by the API's
 * newestComparator — (versionId, createdAt, submissionId) ascending, NOT
 * createdAt alone. Mirrored so the button only shows on the card the server
 * would accept; a race still 409s 'stale_submission' server-side.
 */
function newerOfType(a: SubmissionRow, b: SubmissionRow): SubmissionRow {
  // A withdrawn corrected condition report never counts as newest (the API's
  // newestComparator rule): the first version stands again.
  const aw = a.correction === 'withdrawn';
  const bw = b.correction === 'withdrawn';
  if (aw !== bw) return aw ? b : a;
  if (a.versionId !== b.versionId) return a.versionId < b.versionId ? b : a;
  const ac = a.createdAt ?? '';
  const bc = b.createdAt ?? '';
  if (ac !== bc) return ac < bc ? b : a;
  return (a.docusealSubmissionId ?? 0) >= (b.docusealSubmissionId ?? 0) ? a : b;
}

function loadError(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; message?: string } | null;
    if (err.status === 404) return "That item doesn't exist, or it has been removed.";
    if (err.status === 403) {
      return (
        body?.message ??
        body?.error ??
        'This record is restricted. Access is limited to the staff who handle it.'
      );
    }
    return body?.message ?? body?.error ?? `Couldn't load the item (HTTP ${err.status}).`;
  }
  return err instanceof Error ? err.message : "Couldn't load the item.";
}

/**
 * Everything the page SHOWS, given a detail payload.
 *
 * Split out from the fetching wrapper for the same reason W4 gave `SentItemPanel`
 * a `detail` prop: a self-fetching component renders "Loading…" under
 * `react-dom/server` and is therefore not assertable at all. This is the half
 * the render tests exercise, so "the blocks came out in the §6.4 order and the
 * signing spine is populated" is a test rather than a hope (T11).
 */
export function ItemDetailView({
  detail,
  onReload,
  onSelectVersion,
  onNavigate,
}: {
  detail: SentItemDetailResponse;
  /** The payload changed under us — re-fetch. */
  onReload: () => void;
  /** Switching a version REFETCHES through the caller (T10). */
  onSelectVersion: (versionId: string) => void;
  /** Router navigation, injected so the view has no router dependency of its
   *  own beyond the <Link>s it renders. */
  onNavigate: (to: string) => void;
}): React.JSX.Element {
  const load = onReload;
  const navigate = onNavigate;

  const submissions = useMemo(() => {
    const rows = detail?.submissions ?? [];
    // Newest first — the signing spine reads top-down from the live version.
    return [...rows].sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));
  }, [detail]);

  const newestOfTypeKey = useMemo(() => {
    const newestByType = new Map<string, SubmissionRow>();
    for (const sub of submissions) {
      const cur = newestByType.get(sub.documentType);
      newestByType.set(sub.documentType, cur ? newerOfType(cur, sub) : sub);
    }
    const out = new Map<string, string>();
    for (const [dt, sub] of newestByType) out.set(dt, sub.submissionKey);
    return out;
  }, [submissions]);

  const { row } = detail;

  // The withheld stub, as on the list: type only, and no door into the record.
  // /api/sent-items omits restricted rows rather than stubbing them, so this
  // should be unreachable — it is the net, not the mechanism (§5.7).
  if (row.redacted) {
    return (
      <div className="oc-page">
        <nav className="crumbs" aria-label="Breadcrumb">
          <Link to="/sent-items">Sent Items</Link>
          <span className="crumb-sep" aria-hidden="true" />
          <span aria-current="page">{row.kindLabel}</span>
        </nav>
        <OcEmpty>{RESTRICTED_ROW_NOTE}</OcEmpty>
      </div>
    );
  }

  const inMotion = rowInMotion(row);
  const where = whereLabel(row);
  const casePath =
    row.caseId && row.caseKind === 'owner'
      ? `/owners/${encodeURIComponent(row.caseId)}`
      : row.caseId
        ? `/households/${encodeURIComponent(row.caseId)}`
        : null;

  // §6.4's order, with the DOCUMENT slot taken by the whole viewer. In motion
  // the document is last, so nothing follows; finished, recipients and activity
  // come after it — hence two panel segments rather than one.
  const order = panelBlockOrder(inMotion);
  const cut = order.indexOf('document');
  const beforeDoc: PanelBlock[] = cut === -1 ? [...order] : order.slice(0, cut);
  const afterDoc: PanelBlock[] = cut === -1 ? [] : order.slice(cut + 1);

  // The document the viewer opens on.
  //
  // A doc id addresses the GROUP, not a version: `projectSignatureDocument` sets
  // it to the groupId (to the versionId only for a W-9, T25), and a specific
  // version is selected with `?version=` on GET /api/docs/:kind/:id. So the pair
  // is (docKind, docId) + `initialVersionId` — composing the version into the id
  // produces something the loader cannot resolve.
  //
  // An intake has no versions and no document of its own — its sections are the
  // documents, and the panel already renders each section's replica (T23).
  const current = detail.versions.find((v) => v.current) ?? detail.versions[0] ?? null;
  // `selectedVersionId` is the contract's field for "the version this payload
  // describes" (§7.2) and already reflects `?v=`; `current` is the fallback for
  // a payload that omits it.
  const docTarget = current
    ? {
        kind: current.docKind,
        id: current.docId,
        versionId: detail.selectedVersionId || current.id,
      }
    : null;

  const leaseLifecycle = row.kind === 'lease';
  const builderHref =
    detail.actions?.find((a) => a.id === 'builder' && a.method === 'navigate')?.endpoint ?? null;
  // The submissions all belong to this one item, so they all carry the same
  // thread id (snapshot.ts sets it per record). W2: if the detail response ever
  // grows a top-level `threadId`, prefer that over reading it off a submission.
  const threadId = submissions.find((s) => s.threadId)?.threadId ?? null;

  const onRevised = () => {
    if (builderHref) navigate(builderHref);
    else load();
  };

  const onModifyEpma = (groupId: string, mode: EpmaModifyMode) => {
    navigate(
      `/send/property-management-agreement?from=${encodeURIComponent(groupId)}&mode=${mode}`,
    );
  };

  const panelProps = {
    detail,
    onChanged: load,
    // §6.5: every activity entry, not the drawer's eight.
    activityCap: Number.POSITIVE_INFINITY,
    activityStartsOpen: true,
    showHeader: false,
  } as const;

  return (
    <div className="oc-page" data-motion={inMotion ? 'in-motion' : 'finished'}>
      {/* ---------- Breadcrumb ----------
          Sent Items › the household › this item. The household's NAME is the
          people on it (§7.4 `name (people)`), which is also the item's subject —
          so the subject is the household crumb and the kind is the leaf, rather
          than printing the same names twice. With no case behind the item (a
          W-9 emits caseId null on purpose, T25) the middle crumb is dropped. */}
      <nav className="crumbs" aria-label="Breadcrumb">
        <Link to="/sent-items">Sent Items</Link>
        <span className="crumb-sep" aria-hidden="true" />
        {casePath ? (
          <>
            <Link to={casePath}>{row.subject}</Link>
            <span className="crumb-sep" aria-hidden="true" />
            <span aria-current="page">{row.kindLabel}</span>
          </>
        ) : (
          <span aria-current="page">{row.subject}</span>
        )}
      </nav>

      {/* ---------- Identity, standing, rail ---------- */}
      <header className="oc-panel">
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <SentItemIcon glyph={glyphForKind(row.kind)} />
          <span className="oc-kicker">{row.kindLabel}</span>
          {row.isRenewal && <span className="tag">Renewal</span>}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 'var(--space-3)',
            flexWrap: 'wrap',
            marginTop: 'var(--space-1)',
          }}
        >
          <h1 className="oc-page-title">{row.subject}</h1>
          <StatusChip tone={rowStateTone(row)}>{row.stateLabel}</StatusChip>
        </div>
        {where && <div className="oc-meta">{describeWhere(row.propertyLabel, row.unitLabel)}</div>}

        <div style={{ ...ROW, alignItems: 'baseline', marginTop: 'var(--space-2)' }}>
          <span className="si-standing-step">{row.lifecycle.label}</span>
          {!inMotion && row.completedAt && (
            <span className="oc-meta" title={row.completedAt}>
              finished {relativeTime(row.completedAt)}
            </span>
          )}
        </div>
        {row.waitingOn && (
          <div className="si-waiting">
            Waiting on <b>{row.waitingOn.label}</b>
          </div>
        )}
        {row.lifecycle.detail && (
          <div
            className={row.lifecycle.blocked ? 'si-standing-blocked' : 'oc-meta'}
            {...(row.lifecycle.blocked ? { role: 'status' } : {})}
          >
            {row.lifecycle.detail}
          </div>
        )}
        {/* Signals — the things themselves, not a tally (sol finding 16).
            A count told the operator an item had one blocking signal and gave
            them no way to read it, acknowledge it, snooze it, assign it or
            clear it from the item they were looking at. `SignalRowView` is the
            thread page's own control, calling the same /api/cc/signals/*
            mutations; `onChanged` re-reads the item so the counts on the list
            row behind it move too.

            The gate is the LIST, not the counts: an acknowledged or snoozed
            signal is excluded from every count but stays visible (grayed), and
            it is exactly the one an operator may need to un-snooze. Counts stay
            on the compact list rows, where a row cannot render a paragraph. */}
        {detail.signals && detail.signals.length > 0 && (
          <div className="si-standing-signals" data-section="signals">
            <div className="oc-kicker">Signals</div>
            {detail.signals.map((sig) => (
              <SignalRowView key={sig.id} signal={sig} onChanged={load} />
            ))}
          </div>
        )}

        {/* Occupancy-document sync — same chip + Retry semantics as the Requests
            dashboard (retry re-arms a fresh attempt, never a duplicate). */}
        {detail.appfolioSync && (
          <div style={{ marginTop: 'var(--space-3)' }}>
            <AppfolioSyncChip sync={detail.appfolioSync} onRetried={load} />
          </div>
        )}

        {detail.rail && detail.rail.length > 0 && (
          <div style={{ marginTop: 'var(--space-3)' }} data-section="rail">
            <LifecycleRailView steps={detail.rail} />
          </div>
        )}

        {/* A finished item has no "where it stands" block to hang its actions on
            (§6.4's finished order drops it), so the few it still has — a copy to
            download, the household — live here instead. In-motion actions stay
            with the standing block, where the question they answer is. */}
        {/* A lease can be READ on its own page: header, Review summary card on
            top, the lease inline below (/leases/:groupId). Leases only. */}
        {row.kind === 'lease' && row.groupId && (
          <div style={ROW}>
            <Link
              className="btn"
              data-variant="secondary"
              data-size="sm"
              to={leaseViewPath(row.groupId)}
            >
              View lease
            </Link>
          </div>
        )}

        {!inMotion && detail.actions && detail.actions.length > 0 && (
          <div style={ROW}>
            {detail.actions.map((a) =>
              a.method === 'navigate' ? (
                <Link key={a.id} className="btn" data-variant="secondary" data-size="sm" to={a.endpoint}>
                  {a.label}
                </Link>
              ) : null,
            )}
          </div>
        )}
        {/* A cancelled request is finished, so it has no standing block to carry "Ask again". */}
        {!inMotion && restartActions(detail.actions ?? []).length > 0 && (
          <div data-section="restart">
            <ActionButtons actions={restartActions(detail.actions ?? [])} onChanged={load} />
          </div>
        )}
      </header>

      {/* ---------- Owner intake — the pre-signature management surface ----------
          Only the `owner_intake` row carries one, and only while the record
          exists. It sits directly under the header because it IS where this
          item stands: nothing has been sent for signature yet, and everything
          that can be done about that is here. */}
      {detail.ownerIntake && (
        <ItemOwnerIntakeBlock intake={detail.ownerIntake} onChanged={load} />
      )}

      {/* ---------- Owner contact form — out for the owners ----------
          Carried on the agreement because the form has no item of its own until
          an owner completes it (WEL-181/182). Present only while it is out. */}
      {detail.ownerContactForm && (
        <ItemOwnerContactFormBlock form={detail.ownerContactForm} onChanged={load} />
      )}

      {/* ---------- Condition report: the corrected version ----------
          "Request new signatures" and where a correction stands. It sits with
          the header because it IS where the report stands while one is out. */}
      {detail.conditionReportCorrection && (
        <ConditionReportCorrectionBlock panel={detail.conditionReportCorrection} onChanged={load} />
      )}

      {/* ---------- Versions ----------
          The strip lives on the page rather than inside the panel: the panel's
          header is suppressed here (a "Full Details" button on the Full Details
          page is a link to itself), and switching a version REFETCHES through
          the URL rather than re-labelling what is on screen (T10). */}
      {row.versionCount > 1 && detail.versions.length > 1 && (
        <ViewDocVersionStrip
          versions={stripVersions(detail)}
          selectedId={detail.selectedVersionId || null}
          onSelect={onSelectVersion}
        />
      )}

      {/* ---------- The blocks before the document ---------- */}
      <SentItemPanel {...panelProps} blocks={beforeDoc} />

      {/* ---------- The document itself ----------
          The SHARED WEL-144 viewer (T30: a third mount, composed, not forked) —
          the document inline in whatever state it is in, its own versions and
          activity beside it, the replica for form kinds, and fill-on-behalf
          where the record allows it. The page header above already states who
          and where, so the viewer's own header is suppressed. */}
      {docTarget && (
        <section className="oc-panel" data-block="document">
          <ViewDocViewer
            key={`${docTarget.kind} ${docTarget.id} ${docTarget.versionId ?? ''}`}
            kind={docTarget.kind}
            id={docTarget.id}
            initialVersionId={docTarget.versionId}
            showHeader={false}
            documentOnly
          />
        </section>
      )}

      {/* ---------- The blocks after the document (finished only) ---------- */}
      {afterDoc.length > 0 && <SentItemPanel {...panelProps} blocks={afterDoc} />}

      {/* ---------- The signing spine ---------- */}
      {detail.submissions !== undefined && (
        <section className="oc-panel" data-section="submissions">
          <div className="oc-kicker">Submissions — the signing spine</div>
          {submissions.length === 0 ? (
            row.kind === 'property_management_agreement' && row.groupId ? (
              // The EPMA's intake phase is not missing — it is its OWN row
              // (`owner_intake:<groupId>`, §5.1), because the signature record
              // only exists once an owner completes the intake. Point at it
              // rather than re-implementing the intake panel here.
              <div className="oc-meta">
                Nothing has been sent for signature yet — the agreement is generated once an owner
                completes the intake.{' '}
                <Link to={`/items/${encodeURIComponent(`owner_intake:${row.groupId}`)}`}>
                  Open the owner intake
                </Link>
                .
              </div>
            ) : (
              <OcEmpty>Nothing has been sent for signature on this item.</OcEmpty>
            )
          ) : (
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
            >
              {submissions.map((sub, i) => (
                <ItemSubmissionCard
                  key={sub.submissionKey}
                  submission={sub}
                  threadId={sub.threadId ?? threadId}
                  leaseLifecycle={leaseLifecycle}
                  newest={i === 0}
                  newestOfType={newestOfTypeKey.get(sub.documentType) === sub.submissionKey}
                  viewable={row.viewable}
                  onChanged={load}
                  onRevised={onRevised}
                  onModifyEpma={onModifyEpma}
                />
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ItemPage — the route: fetch, then hand the payload to the view above.
// ---------------------------------------------------------------------------

export function ItemPage(): React.JSX.Element {
  const itemId = useParams().itemId!;
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const version = searchParams.get('v');

  const [detail, setDetail] = useState<SentItemDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const liveRef = useRef(0);

  const load = useCallback(async () => {
    const token = ++liveRef.current;
    setLoading(true);
    setError(null);
    try {
      // §7.2 is the ONE source for this page: the row, the panel of the selected
      // version, recipients, activity, actions, the rail and the submissions,
      // merged server-side. Never /api/cc/thread from here.
      const payload = await fetchSentItem(itemId, version);
      if (liveRef.current !== token) return;
      setDetail(payload);
    } catch (err) {
      if (liveRef.current !== token) return;
      // WEL-167: a property management agreement that does not exist yet is an
      // owner intake still out (the retired /threads/owner_agreement:… links
      // and old bookmarks land here). Open the intake item instead of 404ing.
      if (
        err instanceof ApiError &&
        err.status === 404 &&
        itemId.startsWith('property_management_agreement:')
      ) {
        navigate(
          `/items/${encodeURIComponent(`owner_intake:${itemId.slice('property_management_agreement:'.length)}`)}`,
          { replace: true },
        );
        return;
      }
      setError(loadError(err));
    } finally {
      if (liveRef.current === token) setLoading(false);
    }
  }, [itemId, version]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading && !detail) return <OcLoading label="Loading the item…" />;
  if (error && !detail) {
    // A 403 here is the RRA guard WORKING (§5.7). `loadError` says so in the
    // server's own words; "Back to Sent Items" is the useful move, and Retry is
    // offered because a network failure looks the same from here.
    return (
      <OcError>
        <div>{error}</div>
        <div style={{ ...ROW, marginTop: 'var(--space-3)' }}>
          <Link className="btn" data-variant="secondary" data-size="sm" to="/sent-items">
            Back to Sent Items
          </Link>
          <button
            type="button"
            className="btn"
            data-variant="secondary"
            data-size="sm"
            onClick={() => void load()}
          >
            Retry
          </button>
        </div>
      </OcError>
    );
  }
  if (!detail) return <OcEmpty>Nothing to show for this item.</OcEmpty>;

  return (
    <ItemDetailView
      detail={detail}
      onReload={() => void load()}
      onNavigate={(to) => void navigate(to)}
      onSelectVersion={(versionId) =>
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set('v', versionId);
          return next;
        })
      }
    />
  );
}
